import { z } from 'zod';
import { apropriacaoEntradaSchema, type ApropriacaoResponse } from './custos-pessoas.js';
import { competencia } from './centros-custo.js';
import { cuid, dinheiro, isoDate, paginacao } from './primitives.js';

/**
 * Sessao 9: lancamentos de CIF, configuracoes de rateio e rateio (custo por minuto). docs/SPEC-RATEIO.md.
 * Dinheiro e percentual: string decimal (percentual = fracao). Sem `custo.ver` as chaves de R$ NAO existem na resposta (PCP).
 */

// ---------- CIF ----------

const cifBase = z.object({
  identificador: z.string().trim().min(1).max(60),
  descricao: z.string().trim().min(1).max(200),
  numeroDocumento: z.string().trim().max(60).nullish(),
  competencia,
  vencimento: isoDate.nullish(),
  valor: dinheiro,
  fornecedorId: cuid.nullish(),
  apropriacao: apropriacaoEntradaSchema.nullish(),
});
/**
 * Criar CIF: direta (identificador e descricao) ou a partir de um item de CIF (`itemCifId`: herda nome e apropriacao padrao; o valor e digitado).
 * `centroId` e o atalho "vai direto para este centro" (apropriacao MANUAL 100%); vale no lugar de `apropriacao`.
 */
export const criarCifSchema = cifBase
  .extend({
    identificador: cifBase.shape.identificador.optional(),
    descricao: cifBase.shape.descricao.optional(),
    itemCifId: cuid.optional(),
    centroId: cuid.optional(),
  })
  .refine((v) => v.itemCifId || (v.identificador && v.descricao), { message: 'Informe identificador e descrição, ou escolha um item de CIF', path: ['identificador'] })
  .refine((v) => !(v.apropriacao && v.centroId), { message: 'Informe a apropriação ou o centro, não os dois', path: ['centroId'] });
export type CriarCifDto = z.infer<typeof criarCifSchema>;
export const atualizarCifSchema = cifBase.partial();
export type AtualizarCifDto = z.infer<typeof atualizarCifSchema>;
export const listarCifsSchema = paginacao.extend({ competencia: competencia.optional(), q: z.string().trim().max(100).optional() });
export type ListarCifsQuery = z.infer<typeof listarCifsSchema>;

/** Copiar o mes: de `origem` para `destino`. VALORES copia os valores; ZERADO lanca tudo com valor 0 (para preencher). */
export const COPIA_CIF_MODOS = ['VALORES', 'ZERADO'] as const;
export const copiarCifsSchema = z.object({ origem: competencia, destino: competencia, modo: z.enum(COPIA_CIF_MODOS) });
export type CopiarCifsDto = z.infer<typeof copiarCifsSchema>;
export const previaCopiaCifsSchema = z.object({ origem: competencia, destino: competencia });
export type PreviaCopiaCifsQuery = z.infer<typeof previaCopiaCifsSchema>;

export interface PreviaCopiaCifsResponse {
  origem: string;
  destino: string;
  /** Rateio do destino FECHADO: a copia e recusada. */
  destinoFechado: boolean;
  /** CIFs da origem. */
  total: number;
  /** As que serao criadas e as que ja existem no destino (mesmo identificador) e serao puladas. */
  novas: Array<{ identificador: string; descricao: string }>;
  puladas: Array<{ identificador: string; descricao: string }>;
  /** So com custo.ver: soma dos valores da origem e das que serao criadas (com VALORES). */
  somaOrigem?: string;
  somaNovas?: string;
}

/**
 * Copiar do mes com grade editavel (S27): `GET /cifs/copiar/grade` devolve TODAS as CIFs da origem; `POST /cifs/copiar/lote` grava, numa
 * transacao, so as linhas marcadas, com o NOVO VALOR e (opcional) a apropriacao editada de cada uma. Exige `rateio.editar` + `custo.ver`.
 */
export interface GradeCopiaCifsLinha {
  cifId: string;
  identificador: string;
  descricao: string;
  itemCifNome: string | null;
  valor: string;
  apropriacao: ApropriacaoResponse | null;
  /** Ja existe CIF com este identificador no destino: a linha sera pulada. */
  existeNoDestino: boolean;
}
export interface GradeCopiaCifsResponse {
  origem: string;
  destino: string;
  destinoFechado: boolean;
  linhas: GradeCopiaCifsLinha[];
  totalOrigem: string;
  /** Soma do que o destino ja tem. */
  totalDestinoAtual: string;
}
export const copiarCifsLoteSchema = z.object({
  origem: competencia,
  destino: competencia,
  linhas: z
    .array(z.object({ cifId: cuid, valor: dinheiro, apropriacao: apropriacaoEntradaSchema.optional() }))
    .min(1, 'Marque ao menos uma linha')
    .max(1000)
    .refine((ls) => new Set(ls.map((l) => l.cifId)).size === ls.length, { message: 'Linha repetida' }),
});
export type CopiarCifsLoteDto = z.infer<typeof copiarCifsLoteSchema>;
export interface CopiaCifsLoteResponse extends CopiaCifsResponse {
  /** Soma dos valores gravados no destino por esta copia. */
  totalCopiado: string;
  /** Linhas puladas (identificador ja existe no destino). */
  pulados: Array<{ identificador: string; descricao: string }>;
}

export interface CopiaCifsResponse {
  copiadas: number;
  puladas: number;
  /** Copiadas sem a apropriacao (centro ou criterio que nao vale mais): precisam ser apropriadas a mao. */
  semApropriacao: number;
}

export interface CifResponse {
  id: string;
  identificador: string;
  descricao: string;
  numeroDocumento: string | null;
  competencia: string;
  vencimento: string | null;
  fornecedorId: string | null;
  fornecedorNome: string | null;
  bloqueada: boolean;
  apropriacao: ApropriacaoResponse | null;
  /** Competencia (AAAA-MM) de onde esta CIF foi copiada; null se foi lancada direto. */
  copiadaDe: string | null;
  /** Item de CIF recorrente de onde nasceu o lancamento. */
  itemCifId: string | null;
  itemCifNome: string | null;
  /** So com custo.ver. */
  valor?: string;
}
export interface CifListaResponse {
  itens: CifResponse[];
  total: number;
  /** Soma dos valores da competencia filtrada (rodape). So com custo.ver. */
  somaValor?: string;
}

// ---------- Configuracao de rateio ----------

export const criarConfiguracaoRateioSchema = z.object({
  centroId: cuid,
  /** Posicao na sequencia; padrao = ultima. As demais deslocam. */
  ordem: z.number().int().min(1).max(999).optional(),
  apropriacao: apropriacaoEntradaSchema,
  validFrom: isoDate.optional(),
});
export type CriarConfiguracaoRateioDto = z.infer<typeof criarConfiguracaoRateioSchema>;
export const atualizarConfiguracaoRateioSchema = z.object({ apropriacao: apropriacaoEntradaSchema, validFrom: isoDate.optional() });
export type AtualizarConfiguracaoRateioDto = z.infer<typeof atualizarConfiguracaoRateioSchema>;
export const reordenarConfiguracoesSchema = z.object({ ids: z.array(cuid).min(1).max(200) });
export type ReordenarConfiguracoesDto = z.infer<typeof reordenarConfiguracoesSchema>;
export const destinosConfiguracaoQuery = z.object({ centroId: cuid.optional(), ordem: z.coerce.number().int().min(1).max(999).optional() });
export type DestinosConfiguracaoQuery = z.infer<typeof destinosConfiguracaoQuery>;

export interface ConfiguracaoRateioResponse {
  id: string;
  ordem: number;
  centroId: string;
  centroCodigo: string;
  centroNome: string;
  centroCaminho: string;
  tipo: 'MANUAL' | 'IGUAL' | 'CRITERIO';
  criterioNome: string | null;
  apropriacao: ApropriacaoResponse;
  validFrom: string;
}
export interface CentroDestinoResponse {
  id: string;
  codigo: string;
  nome: string;
  caminho: string;
  classificacao: 'TRANSFORMACAO' | 'APOIO';
}
export interface DestinosConfiguracaoResponse {
  /** Centros de apoio ainda sem configuracao vigente (podem ser escolhidos como origem). */
  apoiosSemConfiguracao: CentroDestinoResponse[];
  /** Destinos validos para a ordem informada: transformacao + apoios de ordem posterior. */
  destinos: CentroDestinoResponse[];
  proximaOrdem: number;
}

// ---------- Rateio ----------

export const rateioCompetenciaParam = z.object({ competencia });
export type RateioCompetenciaParam = z.infer<typeof rateioCompetenciaParam>;

/** Reabrir rateio fechado: a confirmacao digitada (REABRIR) viaja no corpo. */
export const reabrirRateioSchema = z.object({ confirmacao: z.literal('REABRIR') });
export type ReabrirRateioDto = z.infer<typeof reabrirRateioSchema>;
export const listarRateiosSchema = z.object({ ano: z.coerce.number().int().min(2000).max(2100).optional() });

export const RATEIO_STATUS = ['SEM_RATEIO', 'ABERTO', 'FECHADO'] as const;
export type RateioStatusResponse = (typeof RATEIO_STATUS)[number];

export interface DestinoValorResponse {
  centroId: string | null;
  /** Rotulo do centro ("Nao alocado" quando nulo). */
  centroNome: string;
  percentual: string;
  /** So com custo.ver. */
  valor?: string;
}
export interface RateioCifLinha {
  id: string;
  identificador: string;
  descricao: string;
  bloqueada: boolean;
  destinos: DestinoValorResponse[];
  valor?: string;
}
export interface RateioSalarioLinha {
  /** Id do parametro de trabalho vigente (a vigencia usada). */
  parametroId: string;
  pessoaId: string;
  nome: string;
  centroPrincipal: string | null;
  terceirizado: boolean;
  destinos: DestinoValorResponse[];
  salario?: string;
  /** Gratificacoes e adicionais (entram nos encargos). */
  gratificacoes?: string;
  /** Bonus e beneficios (sem encargos). */
  bonus?: string;
  encargos?: string;
  /** Total a ratear (com encargos, exceto terceirizado). */
  total?: string;
}
export interface RateioDepreciacaoLinha {
  imobilizadoId: string;
  descricao: string;
  tipo: string;
  quantidade: string;
  vidaUtilMeses: number;
  idadeMeses: number;
  destinos: DestinoValorResponse[];
  valorUnitario?: string;
  /** quantidade x valor unitario. */
  valorTotal?: string;
  /** valor total / vida util. */
  mensal?: string;
}
export interface RateioCentroColuna {
  id: string;
  codigo: string;
  nome: string;
  natureza: 'ADMINISTRATIVO' | 'PRODUTIVO';
  classificacao: 'TRANSFORMACAO' | 'APOIO' | null;
}
/** Sessao 44: coluna do mapa (so centros PRODUTIVOS: apoio primeiro, na ordem do rateio, depois a producao). */
export interface RateioMapaColuna {
  id: string;
  codigo: string;
  nome: string;
  grupo: 'APOIO' | 'PRODUCAO';
}
export interface RateioMapaLinha {
  chave: string;
  rotulo: string;
  /** Sessao 44: forma de rateio (Critério: X, Percentual manual, Divisão igual, Diversos). So no mapa de custos indiretos. */
  forma?: string;
  /** Posicao do apoio (so no mapa de absorcao). */
  ordem?: number;
  /** centroId -> valor ('' = nao alocado). So com custo.ver. */
  celulas?: Record<string, string>;
  total?: string;
}
export interface RateioCpmLinha {
  centroId: string;
  codigo: string;
  nome: string;
  minutos: string;
  minutosEfetivos: string;
  ociosidade: string;
  avisos: string[];
  acumulado?: string;
  mod?: string;
  cpm?: string;
  modPorMinuto?: string;
}
export interface RateioResponse {
  competencia: string;
  status: RateioStatusResponse;
  calculadoEm: string | null;
  fechadoEm: string | null;
  centros: RateioCentroColuna[];
  /** Sessao 44: colunas dos dois mapas (so centros produtivos, agrupados em apoio e producao). */
  colunasMapa: RateioMapaColuna[];
  /** Sessao 44: a aba Conferencia (e o relatorio dela) so existe com o sinalizador ligado na API (RATEIO_CONFERENCIA_CPM=on); desligado por padrao. */
  conferenciaCpmAtiva: boolean;
  cifs: RateioCifLinha[];
  salarios: RateioSalarioLinha[];
  depreciacoes: RateioDepreciacaoLinha[];
  mapaCif: RateioMapaLinha[];
  mapaAbsorcao: RateioMapaLinha[];
  custoPorMinuto: RateioCpmLinha[];
  /**
   * Conferencia dos mapas (calculada na API a partir dos totais do motor): "Mapa de custos indiretos" (total das entradas) x
   * "Rateio por absorcao" (absorvido pelos centros de transformacao + despesas administrativas + nao alocado). `ok` = diferenca < R$ 0,01.
   * So com custo.ver.
   */
  conferencia?: {
    mapaCustosIndiretos: string;
    rateioAbsorcao: string;
    diferenca: string;
    ok: boolean;
    /** Por que ha valor fora dos centros de transformacao (nao alocado por origem) e a diferenca, se houver. */
    motivos: { codigo: string; mensagem: string; valor: string }[];
  };
  /** Totais por coluna dos mapas (rodape; calculados na API). So com custo.ver. */
  rodapeMapaCif?: { celulas: Record<string, string>; total: string };
  rodapeMapaAbsorcao?: { celulas: Record<string, string>; total: string };
  avisos: { codigo: string; mensagem: string }[];
  /** So com custo.ver. */
  totais?: { entradas: string; despesas: string; custoIndireto: string; naoAlocado: string };
  /** Totais por aba (cards). So com custo.ver. */
  totaisAbas?: { cifs: string; salarios?: string; depreciacoes: string };
  /** CPM medio e minutos acumulados (rodape da aba custo minuto). */
  minutosAcumulados: string;
  cpmMedio?: string;
}
export interface RateioResumoResponse {
  competencia: string;
  status: 'ABERTO' | 'FECHADO';
  /** APP = calculado aqui; XARP = importado do legado (entra FECHADO). */
  origem: 'APP' | 'XARP';
  fechadoEm: string | null;
  totalNaoAlocado?: string;
  totalCustoIndireto?: string;
}

/** Sessao 16: custo de pessoal por centro de custo da competencia (alocacao direta, so totais; nenhum salario individual). */
export interface CustoPessoalCentroLinha {
  centroId: string;
  codigo: string;
  nome: string;
  /** Pessoas com parte do custo neste centro. */
  pessoas: number;
  terceirizados: number;
  total: string;
}
export interface CustoPessoalResponse {
  competencia: string;
  centros: CustoPessoalCentroLinha[];
  /** Custo de pessoas sem apropriacao a centro. */
  semCentro: { pessoas: number; total: string };
  total: string;
  /** Pessoas que entram no mes: vigencia cobrindo o ultimo dia da competencia (ou hoje, no mes corrente). */
  pessoas: number;
}

/** Gerar os lancamentos do mes a partir dos itens de CIF ativos (valores zerados; itens que ja tem lancamento no mes sao pulados). */
export const gerarCifsDoMesSchema = z.object({ competencia });
export type GerarCifsDoMesDto = z.infer<typeof gerarCifsDoMesSchema>;
export interface GerarCifsDoMesResponse {
  geradas: number;
  puladas: number;
  /** Geradas sem apropriacao (o item nao tem apropriacao padrao): precisam ser apropriadas a mao. */
  semApropriacao: number;
}

// ---------- Conferencia do CPM (Sessao 16, item 3) ----------

const referenciaCpm = dinheiro;
export const salvarConferenciaCpmSchema = z.object({
  /** Fracao (0.01 = 1%). Ausente = mantem a atual (padrao 1%, herdada da conferencia anterior). */
  tolerancia: z.string().regex(/^(0|1)(\.\d{1,8})?$|^0?\.\d{1,8}$/, 'Tolerância inválida (fração, até 8 casas)').optional(),
  linhas: z
    .array(
      z.object({
        centroId: cuid,
        /** CPM de referencia da planilha do cliente; null apaga a referencia da linha. */
        referencia: referenciaCpm.nullable(),
        explicacao: z.string().trim().max(500).nullish(),
      }),
    )
    .max(500),
});
export type SalvarConferenciaCpmDto = z.infer<typeof salvarConferenciaCpmSchema>;

export type SituacaoConferenciaCpm = 'OK' | 'FORA' | 'SEM_REFERENCIA';
export interface ConferenciaCpmLinha {
  centroId: string;
  codigo: string;
  nome: string;
  /** CPM calculado pelo XARP/Triade (rateio da competencia). */
  cpmXarp: string;
  referencia: string | null;
  /** cpmXarp - referencia (R$/min). */
  diferenca: string | null;
  /** (cpmXarp - referencia) / referencia, fracao; null se a referencia e zero ou ausente. */
  diferencaPct: string | null;
  situacao: SituacaoConferenciaCpm;
  explicacao: string | null;
  /** Quem gravou a referencia e quando (ISO). */
  registradoPor: { id: string; nome: string } | null;
  registradoEm: string | null;
}
export interface ConferenciaCpmResponse {
  competencia: string;
  /** RATEIO = lido do rateio gravado da competencia; CALCULADO = rateio ainda nao calculado, previa do motor. */
  origemCpm: 'RATEIO' | 'CALCULADO';
  /** Fracao (0.01 = 1%). */
  tolerancia: string;
  linhas: ConferenciaCpmLinha[];
  resumo: { total: number; ok: number; fora: number; semReferencia: number };
}
